import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/lib/utils";

import {
  RIDER_SALE_PAYMENT_METHOD_LABELS,
  RIDER_SALE_STATUS_BADGE_VARIANT,
  RIDER_SALE_STATUS_LABELS,
} from "./rider-sale-labels";

import type { RiderSale } from "@artisancode/api-types";

export function RiderSaleDetailDialog({
  sale,
  onOpenChange,
}: {
  sale: RiderSale | null;
  onOpenChange: (open: boolean) => void;
}) {
  const lines = sale?.lines.filter(
    (l) => l.dispatchedQty > 0 || l.soldQty > 0 || (l.returnedQty ?? 0) > 0,
  );
  const totalReturned =
    lines?.reduce((acc, l) => acc + (l.returnedQty ?? 0), 0) ?? 0;
  const totalDiscrepancy =
    lines?.reduce(
      (acc, l) =>
        acc +
        (l.discrepancyQty ??
          Math.max(0, l.dispatchedQty - l.soldQty - (l.returnedQty ?? 0))),
      0,
    ) ?? 0;

  return (
    <Dialog open={sale !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Detail Penjualan Rider</DialogTitle>
          {sale && (
            <DialogDescription>
              {sale.riderName} · {sale.date}
              {sale.transactionCode && ` · ${sale.transactionCode}`}
            </DialogDescription>
          )}
        </DialogHeader>

        {sale && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-col gap-1">
              <Badge variant={RIDER_SALE_STATUS_BADGE_VARIANT[sale.status]}>
                {RIDER_SALE_STATUS_LABELS[sale.status]}
              </Badge>
              {sale.status === "cancelled" && sale.cancelReason && (
                <span className="text-xs text-muted-foreground italic">
                  &ldquo;{sale.cancelReason}&rdquo;
                </span>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produk</TableHead>
                  <TableHead className="text-right">Ambil</TableHead>
                  <TableHead className="text-right">Jual</TableHead>
                  <TableHead className="text-right">Kembali</TableHead>
                  <TableHead className="text-right">Selisih</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines?.map((l) => {
                  const ret = l.returnedQty ?? 0;
                  const diff =
                    l.discrepancyQty ??
                    Math.max(0, l.dispatchedQty - l.soldQty - ret);

                  return (
                    <TableRow key={l.productId}>
                      <TableCell className="font-medium">
                        {l.productName}
                      </TableCell>
                      <TableCell className="text-right">
                        {l.dispatchedQty}
                      </TableCell>
                      <TableCell className="text-right">{l.soldQty}</TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {ret}
                      </TableCell>
                      <TableCell className="text-right">
                        {diff > 0 ? (
                          <Badge
                            variant="destructive"
                            className="h-5 px-1 font-mono text-[10px]"
                          >
                            {diff}
                          </Badge>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground">
                            0
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(l.subtotal)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell className="font-medium">Total</TableCell>
                  <TableCell className="text-right font-medium">
                    {sale.totalDispatchedQty}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {sale.totalSoldQty}
                  </TableCell>
                  <TableCell className="text-right font-medium font-mono text-muted-foreground">
                    {totalReturned}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {totalDiscrepancy > 0 ? (
                      <Badge
                        variant="destructive"
                        className="h-5 px-1 font-mono text-[10px]"
                      >
                        {totalDiscrepancy}
                      </Badge>
                    ) : (
                      "0"
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(sale.totalRevenue)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <div className="grid grid-cols-2 gap-3 rounded-lg border p-3">
              <div>
                <p className="text-xs text-muted-foreground">Total Setoran</p>
                <p className="font-medium">
                  {formatCurrency(sale.totalRevenue)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Bonus</p>
                <p className="font-medium">{formatCurrency(sale.totalBonus)}</p>
              </div>
            </div>

            <div>
              <p className="mb-1 text-xs text-muted-foreground">Pembayaran</p>
              {sale.payments.length === 0 ? (
                <p className="text-muted-foreground">Belum ada</p>
              ) : (
                <div className="flex flex-col gap-0.5">
                  {sale.payments.map((p) => (
                    <span key={p.method}>
                      {RIDER_SALE_PAYMENT_METHOD_LABELS[p.method]}:{" "}
                      {formatCurrency(p.amount)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {sale.notes && (
              <div>
                <p className="mb-1 text-xs text-muted-foreground">Keterangan</p>
                <p>{sale.notes}</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
